import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import ExpandIcon from 'assets/icons/expand2.svg';
import MicIcon from 'assets/icons/mic.svg';
import MicOffIcon from 'assets/icons/mic-off.svg';
import MinimizeIcon from 'assets/icons/minimize.svg';
import PauseIcon from 'assets/icons/pause.svg';
import PlayIcon from 'assets/icons/play.svg';
import assistantAvatar from 'assets/images/assistant.jpg';
import { store } from 'store';
import {
  isConnected,
  isConnecting,
  setMicMuted,
  startVoice,
  stopVoice,
} from 'store/assistant/voiceManager';

import * as S from './Player.styles';

const EQ_BAR_COUNT = 7;
const COLLAPSED_POSITION = { x: 20, y: -140 }; // offset from bottom-left

export const Player: FC = observer(() => {
  const { voiceState, isMicMuted, voiceLevels, setAssistantMode, setVoiceState } =
    store.assistantStore;
  const { mode } = store.commonStore;

  const [isCollapsed, setIsCollapsed] = useState(false);

  // ── Position state ─────────────────────────────────────────────────────────
  const [pos, setPos] = useState({ x: 20, y: window.innerHeight - 150 });
  const expandedPosRef = useRef({ x: 20, y: window.innerHeight - 150 });

  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(
    null
  );
  const wasDragged = useRef(false);

  const isActive = voiceState === 'active';
  const isPlayerPaused = mode === 'assistant';

  // ── Drag handlers ──────────────────────────────────────────────────────────
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if ((e.target as HTMLElement).closest('button')) return;

      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
      };
      wasDragged.current = false;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [pos]
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;

    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;

    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      wasDragged.current = true;
    }

    setPos({
      x: Math.max(0, Math.min(window.innerWidth - 60, dragRef.current.origX + dx)),
      y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.origY + dy)),
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    if (dragRef.current && !isCollapsed) {
      expandedPosRef.current = { ...pos };
    }
    dragRef.current = null;
  }, [isCollapsed, pos]);

  // ── Collapse / Expand with position save ───────────────────────────────────
  const handleCollapse = useCallback(() => {
    expandedPosRef.current = { ...pos };
    setIsCollapsed(true);
    setPos({
      x: COLLAPSED_POSITION.x,
      y: window.innerHeight + COLLAPSED_POSITION.y,
    });
  }, [pos]);

  const handleExpand = useCallback(() => {
    setIsCollapsed(false);
    setPos(expandedPosRef.current);
  }, []);

  // ── Voice toggle ───────────────────────────────────────────────────────────
  const togglePlay = useCallback(async () => {
    if (isConnecting()) return;

    if (isConnected()) {
      setAssistantMode(null);
      return;
    }

    setAssistantMode('voice');
    setVoiceState('connecting');
    await startVoice();
  }, [setAssistantMode, setVoiceState]);

  // ── Mic toggle ─────────────────────────────────────────────────────────────
  const toggleMic = useCallback(() => {
    setMicMuted(!isMicMuted);
  }, [isMicMuted]);

  // ── Auto-pause when mode === 'assistant' ───────────────────────────────────
  useEffect(() => {
    if (isPlayerPaused && isConnected()) {
      stopVoice();
    }
  }, [isPlayerPaused]);

  // ── Keep in bounds on resize ───────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => {
      setPos((prev) => ({
        x: Math.min(prev.x, window.innerWidth - 60),
        y: Math.min(prev.y, window.innerHeight - 60),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Collapsed (mini) view ──────────────────────────────────────────────────
  if (isCollapsed) {
    return (
      <S.Wrapper
        $x={pos.x}
        $y={pos.y}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <S.Mini>
          <S.OrbWrap style={{ width: 26, height: 26 }}>
            <S.OrbRing $active={isActive} />
            <S.OrbCore $active={isActive} style={{ width: 12, height: 12 }} />
          </S.OrbWrap>
          <S.ExpandBtn onClick={handleExpand}>
            <ExpandIcon />
          </S.ExpandBtn>
        </S.Mini>
      </S.Wrapper>
    );
  }

  // ── Expanded view ──────────────────────────────────────────────────────────
  const showPlay = voiceState === 'idle' || voiceState === 'error';
  const isButtonDisabled = voiceState === 'connecting' || isPlayerPaused;

  return (
    <S.Wrapper
      $x={pos.x}
      $y={pos.y}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <S.Panel>
        <S.Avatar>
          <img src={assistantAvatar} alt="Sophie" />
        </S.Avatar>

        <S.OrbWrap>
          <S.OrbRing $active={isActive} />
          <S.OrbCore $active={isActive} />
        </S.OrbWrap>

        <S.EqBars $active={isActive}>
          {Array.from({ length: EQ_BAR_COUNT }, (_, i) => (
            <S.EqBar
              key={i}
              $active={isActive}
              $i={i}
              style={
                isActive && voiceLevels
                  ? { transform: `scaleY(${Math.max(0.15, voiceLevels[i] || 0)})` }
                  : undefined
              }
            />
          ))}
        </S.EqBars>

        <S.Controls>
          <S.Btn onClick={togglePlay} disabled={isButtonDisabled}>
            {showPlay ? <PlayIcon /> : <PauseIcon />}
          </S.Btn>
          <S.Btn onClick={toggleMic} disabled={!isActive}>
            {isMicMuted ? <MicOffIcon /> : <MicIcon />}
          </S.Btn>
          <S.Btn onClick={handleCollapse}>
            <MinimizeIcon />
          </S.Btn>
        </S.Controls>
      </S.Panel>
    </S.Wrapper>
  );
});
